import { DatabaseAdd, ThreeDotsVertical } from "react-bootstrap-icons"
import TableHeader from "../Components/TableHeader"
import { useCallback, useEffect, useMemo, useState } from "react"
import TableLoader from "../Components/TableLoader"
import OutsideClickHandler from "react-outside-click-handler"
import TableFoot from "../Components/TableFoot"
import AddNewAccount from "../Components/AddNewAccount"
import DialogModal from "../Components/DialogModal"
import { Modal } from "react-bootstrap"
import api, { isCanceled } from "../lib/api"
import useNotify from "../hooks/useNotify"
import useListData from "../hooks/useListData"
import useTableFilter from "../hooks/useTableFilter"

const EMPTY_FORM = { buyer: "", password: "", comment: "", auction: "" };

const Accounts = ({ setNotification }) => {
    const notify = useNotify(setNotification);
    const { filter, setFilter, clampPage } = useTableFilter({
        page: 1, limit: 10, keyword: "", sort_by: "id", sort_order: "DESC", auction: "",
    });

    const [modals, setModals] = useState({ createAccount: false, deleteAccount: false });
    const [sortBy, setSortBy] = useState([]);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [active, setActive] = useState("");
    const [action, setAction] = useState("add");
    const [dialog, setDialog] = useState({ title: "", text: "", show: false, id: "", loader: false });
    const [changeBuyersForm, setChangeBuyersForm] = useState({ current_buyer: "", new_buyer: "", account: "copart", show: false });
    const [load, setLoad] = useState(false);

    const onListError = useCallback((err) => notify.error(err), [notify]);
    const { data: accounts, total, loading, reload } = useListData("/accounts", filter, { onError: onListError });

    const pageCount = useMemo(() => Math.max(1, Math.ceil(total / filter.limit)), [total, filter.limit]);

    useEffect(() => { clampPage(total); }, [total, clampPage]);

    useEffect(() => {
        const controller = new AbortController();

        api.get('/options', { params: { name: "accounts-sort-by" }, signal: controller.signal })
            .then((res) => {
                const data = res.data.data;
                const parsed = typeof data === "string" ? JSON.parse(data) : data;
                setSortBy(Array.isArray(parsed) ? parsed : []);
            })
            .catch((err) => {
                if (isCanceled(err)) return;
                setSortBy([]);
                notify.error(err, "Could not load sort options.");
            });

        return () => controller.abort();
    }, [notify]);

    const handlers = {
        addNewHandler() {
            setFormData(EMPTY_FORM);
            setAction("add");
            setModals((prev) => ({ ...prev, createAccount: true }));
        },
        openEditAccountModal(acc) {
            setActive("");
            setFormData({ buyer: acc.buyer, password: acc.password ?? "", comment: acc.comment, auction: acc.auction, id: acc.id });
            setAction("edit");
            setModals((prev) => ({ ...prev, createAccount: true }));
        },
        openDeleteAccountModal(acc) {
            setDialog({ show: true, title: "Delete Account", text: "Do you want to remove this account?", id: acc.id, loader: false });
            setActive("");
        },
        testConnection(acc) {
            const url = acc.auction === "copart"
                ? `https://copart.com/login?account=${encodeURIComponent(acc.buyer)}&type=testconnection`
                : `https://login.iaai.com/Identity/Account/Login?account=${encodeURIComponent(acc.buyer)}&type=testconnection`;
            window.open(url, "_blank", "noopener");
            setActive("");
        },
        removeAccount() {
            setDialog((prev) => ({ ...prev, loader: true }));
            api.delete(`/accounts/${dialog.id}`)
                .then((res) => {
                    notify.success(res.data.message.en);
                    reload();
                })
                // Reported failures as a success toast, so a rejected delete
                // looked like it had worked.
                .catch((err) => notify.error(err))
                // The dialog stayed open with its spinner running on failure.
                .finally(() => setDialog({ show: false, title: "", text: "", id: "", loader: false }));
        },
        changeBuyer(e) {
            e.preventDefault();
            setLoad(true);
            api.put('/change-buyer', {
                current_buyer: changeBuyersForm.current_buyer,
                new_buyer: changeBuyersForm.new_buyer,
                account: changeBuyersForm.account,
                bySelected: false,
            })
                .then((res) => {
                    notify.success(res.data.message.en);
                    setChangeBuyersForm({ current_buyer: "", new_buyer: "", account: "copart", show: false });
                    reload();
                })
                .catch((err) => notify.error(err))
                .finally(() => setLoad(false));
        },
    };

    return (
        <div className="page-content-container">
            <h2 className="page-title">Accounts</h2>
            <TableHeader
                addNewHandler={handlers.addNewHandler}
                setNotification={setNotification}
                AddNewButtonIcon={DatabaseAdd}
                setModals={setModals}
                filter={filter}
                sortBy={sortBy}
                exportButton={false}
                checkboxes={[]}
                setFilter={setFilter}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="export-button" style={{ marginTop: "10px" }} onClick={() => setChangeBuyersForm({ current_buyer: "", new_buyer: "", account: "copart", show: true })}>
                    Change Buyers
                </button>
            </div>
            <div className="table-scroll">
                <table className="responsive-d-table">
                    <thead>
                        <tr>
                            <th>Buyer</th>
                            <th>Auction</th>
                            <th>Comment</th>
                            <th><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* The select-all and per-row checkboxes were removed:
                            nothing on this page acts on a selection, and the
                            header box had an empty if/else that did nothing. */}
                        {(!loading && accounts.length > 0) && accounts.map((account, id) => (
                            <tr key={account.id} className={(id + 2) % 2 === 0 ? "even-row" : "odd-row"}>
                                <td>{account.buyer}</td>
                                <td>{account.auction}</td>
                                <td>{account.comment}</td>
                                <td>
                                    <button
                                        type="button"
                                        className="row-actions-trigger"
                                        aria-label={`Actions for ${account.buyer}`}
                                        onClick={() => setActive((prev) => (prev === account.id ? "" : account.id))}
                                    >
                                        <ThreeDotsVertical />
                                    </button>
                                    {active === account.id && <OutsideClickHandler onOutsideClick={() => setActive("")}>
                                        <div className="table-actions-box">
                                            <ul>
                                                <li onClick={() => handlers.openEditAccountModal(account)}>Edit</li>
                                                <li onClick={() => handlers.testConnection(account)}>Test Connection</li>
                                                <li onClick={() => handlers.openDeleteAccountModal(account)}>Delete</li>
                                            </ul>
                                        </div>
                                    </OutsideClickHandler>}
                                </td>
                            </tr>
                        ))}
                        {(!loading && accounts.length === 0) && <tr className="empty-row">
                            <td colSpan={4}><div className="empty-records"><span>Records not found</span></div></td>
                        </tr>}
                        {loading && <TableLoader colspan={4} />}
                    </tbody>
                </table>
            </div>
            <TableFoot filter={filter} setFilter={setFilter} count={pageCount} total={total} />
            <AddNewAccount
                show={modals.createAccount}
                setShow={(value) => setModals((prev) => ({ ...prev, createAccount: value }))}
                formData={formData}
                action={action}
                setFormData={setFormData}
                setNotification={setNotification}
                onSaved={reload}
            />
            <DialogModal
                show={dialog.show}
                setShow={(value) => setDialog((prev) => ({ ...prev, show: value }))}
                title={dialog.title}
                text={dialog.text}
                onSubmit={handlers.removeAccount}
                loader={dialog.loader}
            />
            <Modal show={changeBuyersForm.show} onHide={() => setChangeBuyersForm((prev) => ({ ...prev, show: false }))} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="modal-heading">Change Buyer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="change-buyers-form" onSubmit={handlers.changeBuyer}>
                        <div className="form-group mt-3">
                            <label htmlFor="acc-current-buyer">Current Buyer</label>
                            <input id="acc-current-buyer" required className="form-control" value={changeBuyersForm.current_buyer} onChange={(e) => setChangeBuyersForm((prev) => ({ ...prev, current_buyer: e.target.value }))} placeholder="Current Buyer" />
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="acc-new-buyer">New Buyer</label>
                            <input id="acc-new-buyer" required className="form-control" value={changeBuyersForm.new_buyer} onChange={(e) => setChangeBuyersForm((prev) => ({ ...prev, new_buyer: e.target.value }))} placeholder="New Buyer" />
                        </div>
                        <div className="form-group mt-3">
                            <label htmlFor="acc-buyer-account">Account</label>
                            <select id="acc-buyer-account" className="form-control" value={changeBuyersForm.account} onChange={(e) => setChangeBuyersForm((prev) => ({ ...prev, account: e.target.value }))}>
                                <option value="copart">Copart</option>
                                <option value="iaai">IAAI</option>
                            </select>
                        </div>
                        <p className="form-hint">This updates every user who currently holds that buyer.</p>
                        <button disabled={load} type="submit" className="submit-modal-form-btn" style={{ width: "100%" }}>
                            {load ? "Changing..." : "Change Buyer"}
                        </button>
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default Accounts
